import styled from '@emotion/styled';
import { ContentCopy as ContentCopyIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { Box, Grid, Button as MuiButton, Divider as MuiDivider, Typography, CircularProgress } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import React, { useRef, useState } from 'react';
import {
  BooleanField,
  Button,
  FunctionField,
  ImageField,
  NumberField,
  ReferenceArrayField,
  Show,
  SimpleShowLayout,
  SingleFieldList,
  ChipField,
  Tab,
  TabbedShowLayout,
  TextField,
  useNotify,
  useRecordContext,
  usePermissions,
  useDataProvider,
} from 'react-admin';
import { Permission } from '../../components';
import { PERMISSIONS } from '../../constants';
import ChainStationsTab from './ChainStationsTab';
import StoreVersionPanel from './StoreVersionPanel';

const containerIdFieldSx = {
  display: 'flex',
  padding: 0,
  alignItems: 'center',
};

const containerLinkFieldSx = {
  display: 'flex',
  padding: 0,
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '12px',
};

const noPaddingSx = {
  padding: 0,
};

const Divider = styled(MuiDivider)({
  width: '100%',
  marginTop: 12,
  marginBottom: 12,
});

const IdField = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  const notify = useNotify();

  const handleClipboard = () => {
    if (record.id) {
      navigator.clipboard.writeText(record.id as string);
      notify("Id copiado para clipboard", { type: "info", autoHideDuration: 1000 });
    }
  };

  return (
    <SimpleShowLayout sx={containerIdFieldSx}>
      <TextField source="id" label="ID" />
      <MuiButton onClick={handleClipboard} size="small" variant="outlined" sx={{ marginTop: "20px", marginLeft: "10px" }}>
        <ContentCopyIcon sx={{ fontSize: 18 }} />
      </MuiButton>
    </SimpleShowLayout>
  );
};

const LinkField = ({ url }: { url: string }) => {
  const notify = useNotify();

  const handleClipboard = () => {
    if (url) {
      navigator.clipboard.writeText(url);
      notify("Link copiado para clipboard", { type: "info", autoHideDuration: 1000 });
    }
  };

  return (
    <SimpleShowLayout sx={containerLinkFieldSx}>
      <MuiButton onClick={handleClipboard} size="small" variant="outlined" sx={{ marginTop: "20px", marginRight: "10px" }}>
        <ContentCopyIcon sx={{ fontSize: 18 }} />
      </MuiButton>
      <FunctionField label="Link" render={() => url} />
    </SimpleShowLayout>
  );
};

const QRDownloader = () => {
  const record = useRecordContext();
  const svgRef = useRef<HTMLDivElement>(null);
  if (!record) return null;

  const url = `https://${record.code}-app.pontuax.com.br/download`;

  const downloadBlob = (blob: Blob, filename: string) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadSvg = () => {
    const content = svgRef.current?.children[0]?.innerHTML;
    if (!content) return;
    const contentWithSvg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" viewBox="0 0 29 29" width="600" height="600">
    ${content.replace(/fill="#ffffff"/g, 'fill="none"')}
    </svg>`;
    const blob = new Blob([contentWithSvg], { type: "image/svg+xml" });
    downloadBlob(blob, `qr-code-${record.name}`);
  };

  return (
    <div style={{ textAlign: "center", marginBottom: 8 }}>
      <div ref={svgRef}>
        <QRCodeSVG
          value={url}
          bgColor="#ffffff"
          fgColor="#000000"
          size={256}
        />
      </div>
      <Button color="primary" style={{ marginTop: 8 }} size="medium" label="Baixar SVG" onClick={downloadSvg} variant="outlined" />
      <LinkField url={url} />
    </div>
  );
};

const ModuleField = ({ label, valueSource, enabledSource }: { label: string; valueSource: string; enabledSource: string }) => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Grid container lg={8} md={12} spacing={2} alignItems="center">
      <Grid size={12}>
        <Typography>{label}</Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <SimpleShowLayout>
          <FunctionField
            label="Valor"
            render={(rec: any) => rec[valueSource] ? `R$ ${Number(rec[valueSource]).toFixed(2)}` : 'R$ 0,00'}
          />
        </SimpleShowLayout>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <SimpleShowLayout>
          <BooleanField source={enabledSource} label="Habilitado" />
        </SimpleShowLayout>
      </Grid>
    </Grid>
  );
};

const RunCommentAnalysisButton: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);

  if (!permissions?.includes(PERMISSIONS.ADMIN) || !record?.id) return null;

  const handleRun = async () => {
    setLoading(true);
    try {
      await dataProvider.create(`chains/${record.id}/comment-analysis/run`, { data: {} });
      notify('Análise iniciada. Os resultados aparecerão em breve.', { type: 'success' });
    } catch (error: any) {
      if (error?.status === 409) {
        notify('Análise já está em andamento. Aguarde e tente novamente.', { type: 'warning' });
      } else {
        notify('Erro ao iniciar a análise.', { type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8f7ff', borderRadius: 2, border: '1px solid rgba(120,105,223,0.15)', mt: 2 }}>
      <AutoAwesomeIcon sx={{ color: '#7869DF', fontSize: 20, flexShrink: 0 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Análise de Comentários por IA
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          Processa os comentários não analisados desta rede usando IA
        </Typography>
      </Box>
      <MuiButton
        variant="contained"
        size="small"
        disabled={loading}
        onClick={handleRun}
        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
        sx={{ bgcolor: '#7869DF', '&:hover': { bgcolor: '#5f51c7' }, textTransform: 'none', fontWeight: 600, borderRadius: 2, whiteSpace: 'nowrap' }}
      >
        {loading ? 'Iniciando…' : 'Executar agora'}
      </MuiButton>
    </Box>
  );
};

const ChainShowTabs: React.FC = () => {
  const record = useRecordContext();
  return (
      <TabbedShowLayout>
        <Tab label="Identificação">
          <IdField />
          <TextField source="name" label="Nome da rede/posto" emptyText="--" />
          <TextField source="phone" label="Telefone" emptyText="--" />
          <TextField source="email" label="Email" emptyText="--" />
          <TextField source="contact" label="Contato" emptyText="--" />
          <TextField source="emailNotice" label="Email para Avisos" emptyText="--" />
          <ImageField source="logo.src" label="Logo" />
          <FunctionField
            label="Service Account"
            render={(record: any) => record?.serviceAccount ? 'Configurado' : 'Não configurado'}
          />
          <BooleanField source="isActive" label="Ativo" />
        </Tab>

        <Tab label="Postos">
          <ChainStationsTab />
        </Tab>

        {Permission({
          permission: PERMISSIONS.REGISTER_CHAIN_MODULE,
          children: (
            <Tab label="Módulos">
              <ModuleField label="QrCode" valueSource="qrcodeValue" enabledSource="useQrcode" />
              <Divider />
              <ModuleField label="Promoter" valueSource="promoterValue" enabledSource="usePromoter" />
              <Divider />
              <ModuleField label="Anúncios" valueSource="advertisementValue" enabledSource="useAdvertisement" />
              <Divider />
              <ModuleField label="Sorteios" valueSource="drawingValue" enabledSource="useDrawing" />
              <Divider />
              <ModuleField label="Brinde" valueSource="giftValue" enabledSource="useGift" />
              <Divider />
              <Grid container lg={8} md={12} spacing={2} alignItems="center">
                <Grid size={12}><Typography>Bolão da Copa</Typography></Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <SimpleShowLayout>
                    <BooleanField source="useBolao" label="Habilitado" />
                  </SimpleShowLayout>
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} alignItems="center" sx={{ maxWidth: 800 }}>
                <Grid size={12}><Typography>Roleta da Sorte</Typography></Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <SimpleShowLayout>
                    <BooleanField source="useRoleta" label="Habilitado" />
                  </SimpleShowLayout>
                </Grid>
              </Grid>
              <Divider />
              <Grid container lg={8} md={12} spacing={2} alignItems="center">
                <Grid size={12}><Typography>Recarga Elétrica (InCharge)</Typography></Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <SimpleShowLayout>
                    <BooleanField source="useIncharge" label="Habilitado" />
                  </SimpleShowLayout>
                </Grid>
              </Grid>
              <Divider />
              <ModuleField label="Dotz" valueSource="dotzValue" enabledSource="useDotz" />
              <Divider />
              <ModuleField label="Meta de frentista" valueSource="attendantTargetValue" enabledSource="useAttendantTarget" />
              <Divider />
              <ModuleField label="Limites de Pontuação" valueSource="limitsModuleValue" enabledSource="useLimits" />
            </Tab>
          )
        })}

        <Tab label="Revenda">
          <ReferenceArrayField reference="retailers" source="retailerIds" label="Revendedores">
            <SingleFieldList linkType={false}>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>
        </Tab>

        <Tab label="Aplicativo">
          <Grid container width={{ xs: '100%' }} spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Dados</Typography>
              <SimpleShowLayout sx={noPaddingSx}>
                <TextField source="appName" label="Nome Ícone (Padrão)" emptyText="--" />
                <TextField source="appNameInsideApp" label="Nome Interno" emptyText="--" />
                <TextField source="appNameStore" label="Nome na Loja" emptyText="--" />
                <TextField source="appSlogan" label="Slogan" emptyText="--" />
                <TextField source="appPhone" label="Telefone" emptyText="--" />
              </SimpleShowLayout>

              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Layout</Typography>
              <SimpleShowLayout sx={noPaddingSx}>
                <FunctionField
                  label="Cor principal"
                  render={(record: any) => record?.appColor ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 24, height: 24, backgroundColor: record.appColor, border: '1px solid #ccc', borderRadius: 1 }} />
                      {record.appColor}
                    </Box>
                  ) : '--'}
                />
                <FunctionField
                  label="Cor da fonte na Home"
                  render={(record: any) => record?.appHeaderLabelColor ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 24, height: 24, backgroundColor: record.appHeaderLabelColor, border: '1px solid #ccc', borderRadius: 1 }} />
                      {record.appHeaderLabelColor}
                    </Box>
                  ) : '--'}
                />
                <FunctionField
                  label="Cor do botão de Cadastro"
                  render={(record: any) => record?.appSignUpButtonColor ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 24, height: 24, backgroundColor: record.appSignUpButtonColor, border: '1px solid #ccc', borderRadius: 1 }} />
                      {record.appSignUpButtonColor}
                    </Box>
                  ) : '--'}
                />
                <FunctionField
                  label="Cor do botão de Login"
                  render={(record: any) => record?.appSignInButtonColor ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 24, height: 24, backgroundColor: record.appSignInButtonColor, border: '1px solid #ccc', borderRadius: 1 }} />
                      {record.appSignInButtonColor}
                    </Box>
                  ) : '--'}
                />
                <NumberField source="appLogoWidth" label="Largura da logo na tela Inicial" emptyText="--" />
                <BooleanField source="appShowWelcomeLabel" label="Mostrar mensagem Bem Vindo" />
              </SimpleShowLayout>

              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Sociais</Typography>
              <SimpleShowLayout sx={noPaddingSx}>
                <TextField source="appInstagram" label="Instagram" emptyText="--" />
                <TextField source="appTwitter" label="Twitter" emptyText="--" />
                <TextField source="appYoutube" label="Youtube" emptyText="--" />
                <TextField source="appFacebookPageId" label="Facebook - Id da página" emptyText="--" />
                <TextField source="appFacebook" label="Facebook" emptyText="--" />
              </SimpleShowLayout>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} sx={{ alignSelf: "center" }}>
              <QRDownloader />
            </Grid>
          </Grid>
          <StoreVersionPanel />
        </Tab>

        <Tab label="Configurações">
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Identificação nas Lojas</Typography>
          <SimpleShowLayout sx={noPaddingSx}>
            <TextField source="firebaseProjectId" label="Id do Projeto no Firebase" emptyText="--" />
            <TextField source="googlePackageName" label="Nome do Pacote no Google" emptyText="--" />
            <TextField source="applePackageName" label="Nome do Pacote na Apple" emptyText="--" />
            <TextField source="appleAppId" label="ID do Aplicativo na Apple" emptyText="--" />
            <BooleanField source="useCode2fa" label="Código 2FA habilitado" />
          </SimpleShowLayout>

          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Abastecimento</Typography>
          <SimpleShowLayout sx={noPaddingSx}>
            <BooleanField source="mustInformEncerrante" label="Obrigar a informar encerrante" />
            <BooleanField source="mustInformHoseNumber" label="Obrigar a informar bico" />
            <BooleanField source="requireLocationOnFillin" label="Conferir localização no abastecimento" />
            <BooleanField source="locationConfigEnabled" label="Conferir localização (desativação temporária)" />
            <BooleanField source="appOnlyCompletelyFillin" label="Somente encher tanque" />
            <BooleanField source="useAttendantCode" label="Usa código no frentista" />
            <BooleanField source="useVehicleOdometer" label="Usar odômetro de veículo" />
            <NumberField source="lastFillinsWindowMinutes" label="Tempo máximo do abastecimento (minutos)" emptyText="30 (padrão)" />
          </SimpleShowLayout>

          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Preços e Descontos</Typography>
          <SimpleShowLayout sx={noPaddingSx}>
            <BooleanField source="isPriceIncreaseEnabled" label="Habilitar acréscimo" />
            <BooleanField source="isMultipleDiscountsEnabled" label="Habilitar descontos múltiplos" />
          </SimpleShowLayout>

          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Verificação e Cadastro</Typography>
          <SimpleShowLayout sx={noPaddingSx}>
            <BooleanField source="hasValidationTokenByMessage" label="Enviar código de verificação por SMS/WhatsApp" />
            <BooleanField source="appUseQrCodeForSignup" label="Usar QR Code na verificação de SignUp" />
          </SimpleShowLayout>

          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Dotz</Typography>
          <SimpleShowLayout sx={noPaddingSx}>
            <BooleanField source="sendSalesToDotz" label="Enviar vendas para Dotz" />
          </SimpleShowLayout>

          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Atualização Forçada do App</Typography>
          <SimpleShowLayout sx={noPaddingSx}>
            <NumberField source="appMinBuild" label="Build Mínimo do App" emptyText="--" />
            <BooleanField source="appForceUpdate" label="Forçar atualização (bloquear app)" />
          </SimpleShowLayout>

          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Inteligência Artificial</Typography>
          <Permission permission={PERMISSIONS.ADMIN}>
            <RunCommentAnalysisButton />
          </Permission>
        </Tab>

        {record?.useDotz && (
          <Tab label="Dotz">
            <TextField source="dotzUser" label="Usuário" emptyText="--" />
            <FunctionField
              label="Fator de conversão"
              render={(rec: any) => rec.dotzFactor ? Number(rec.dotzFactor).toFixed(3) : '0,000'}
            />
          </Tab>
        )}
      </TabbedShowLayout>
  );
};

const ChainShow: React.FC = () => {
  return (
    <Show title="Visualizar rede">
      <ChainShowTabs />
    </Show>
  );
};

export default ChainShow;
